import { Network } from '@capacitor/network'
import { Filesystem, Directory } from '@capacitor/filesystem'
import { CloudFile } from '@/lib/CloudFile'
import { TILE_DIR, PHOTO_DIR } from '@/app/constant'
import type { Floorplan } from '@/app/data/types'

type FileType = 'tile' | 'file'

/**
 * 파일 다운로드 작업 (기본 정보)
 */
interface FileDownloadTaskBase {
  taskId: string
  fileUrl: string
  name: string
  sectionId?: string // 섹션 ID (진행률 추적용)
}

/**
 * 파일 다운로드 작업 (타입 포함)
 */
interface FileDownloadTask extends FileDownloadTaskBase {
  type: FileType // 파일 타입 (tile | file)
}

interface DownloadBatch {
  taskId: string
  tasks: FileDownloadTaskBase[] // type 없는 기본 정보만 받음
  type: FileType // batch 레벨에서 type 관리
  sectionId?: string // 섹션 ID
  progressCallback?: ProgressCallback // 섹션별 진행률 콜백
}

// 진행률 콜백 타입
type ProgressCallback = (current: number, total: number, percentage: number) => void

// 섹션 진행률 정보
interface SectionProgress {
  total: number
  completed: number
  callback: ProgressCallback | null
}

/**
 * 전역 파일 다운로드 매니저 (싱글톤)
 * - 단일 task에서 호출
 * - 중복 다운로드 방지
 * - 네트워크 재연결 시 자동 재개
 * - 백그라운드에서 천천히 다운로드
 * - 진행중에 다른과업 진입 시 큐를 초기화 하고 새로진행
 */
export class FileDownloadManager {
  private readonly STORAGE_DIRECTORY = Directory.Cache
  private static instance: FileDownloadManager | null = null

  // 동시 다운로드 수 — 네이티브 직접 저장이라 JS 메모리 부담이 없고 건당 지연이 커서, 첫 진입(A21s)에서
  // 6 → 12로 받기 구간 24.7~35.0s → 16.1~19.5s(16은 12와 차이 ≈1s)
  private readonly BATCH_SIZE = 12
  private readonly RETRY_CHECK_INTERVAL = 10000 // 재시도 체크 간격

  private downloadingFiles = new Set<string>()
  private downloadedFiles = new Set<string>()
  private failedFiles: FileDownloadTask[] = []
  // failedFiles의 fileUrl 집합 (중복 체크 O(1)용, failedFiles와 항상 동기 유지)
  private failedFileUrls = new Set<string>()
  // mkdir 완료된 디렉토리 (다운로드마다 mkdir 반복 방지)
  private ensuredDirs = new Set<string>()
  // 디렉토리별 파일 목록 메모 (과업 세션 동안 유지, 다운로드 완료 시 갱신)
  // — 수만 개 파일이 든 타일 디렉토리를 배치마다 readdir하면 배치당 5~10초의 브리지 비용이 들어
  //   진입 후 수 분간 백그라운드를 점유하므로 세션당 1회만 읽는다
  private dirListingCache = new Map<string, Set<string>>()

  // 디렉토리 파일 목록 (메모이즈). 미존재 디렉토리는 빈 집합
  private async getDirListing(dir: string): Promise<Set<string>> {
    const cached = this.dirListingCache.get(dir)
    if (cached) return cached
    const listing = new Set<string>()
    try {
      const result = await Filesystem.readdir({ path: dir, directory: this.STORAGE_DIRECTORY })
      for (const file of result.files) listing.add(`${dir}/${file.name}`)
    } catch {
      // 디렉토리 미존재 → 빈 목록
    }
    this.dirListingCache.set(dir, listing)
    return listing
  }

  // 다운로드 완료 파일을 목록 메모에 반영
  private addToDirListing(filePath: string): void {
    const dir = filePath.substring(0, filePath.lastIndexOf('/'))
    this.dirListingCache.get(dir)?.add(filePath)
  }

  // 타일 일시정지 게이트 (층 진입 등 우선 작업과의 CPU/네트워크 경쟁 방지)
  // 중첩 suspend 허용을 위해 카운터로 관리
  private tileSuspendCount = 0
  private tileGate: Promise<void> | null = null
  private tileGateResolve: (() => void) | null = null

  // 타일 다운로드 일시정지 (호출한 만큼 resumeTileDownloads 필요)
  suspendTileDownloads(): void {
    this.tileSuspendCount++
    if (!this.tileGate) {
      this.tileGate = new Promise<void>(resolve => {
        this.tileGateResolve = resolve
      })
    }
  }

  // 타일 다운로드 재개
  resumeTileDownloads(): void {
    this.tileSuspendCount = Math.max(0, this.tileSuspendCount - 1)
    if (this.tileSuspendCount === 0) {
      this.releaseTileGate()
    }
  }

  private releaseTileGate(): void {
    this.tileSuspendCount = 0
    this.tileGateResolve?.()
    this.tileGate = null
    this.tileGateResolve = null
  }

  // 일시정지 해제까지 대기 (abort 시 즉시 반환 — 이후 signal 체크로 종료됨)
  private async waitTileResume(signal?: AbortSignal): Promise<void> {
    while (this.tileGate) {
      if (signal?.aborted) return
      await this.tileGate
    }
  }

  // 섹션별 진행률 추적
  private sections = new Map<string, SectionProgress>()

  // 재시도 interval ID
  private retryInterval: ReturnType<typeof setTimeout> | null = null

  // 현재 작업 중인 taskId
  private currentTaskId: string | null = null

  // 과업 전환 시 진행 중 다운로드를 중단하기 위한 AbortController
  private abortController: AbortController | null = null

  // 싱글톤
  private constructor() {}

  static getInstance(): FileDownloadManager {
    if (!FileDownloadManager.instance) {
      FileDownloadManager.instance = new FileDownloadManager()
    }
    return FileDownloadManager.instance
  }

  /**
   * 새로운 과업 시작 (이전 큐 초기화)
   * @param taskId - 과업 ID
   */
  startNewTask(taskId: string): void {
    if (this.currentTaskId && this.currentTaskId !== taskId) {
      this.reset()
    }

    this.currentTaskId = taskId
    this.abortController = new AbortController()
  }

  /**
   * 다운로드 배치 추가
   * @param batch 다운로드 배치
   * @returns 다운로드 완료 Promise
   */
  async addDownloadBatch(batch: DownloadBatch): Promise<void> {
    const { sectionId, progressCallback, tasks, type } = batch

    // 중복 제거 (다운로드 완료, 진행중, 실패 큐 모두 체크)
    const newTasks = tasks
      .filter(
        task =>
          !this.downloadedFiles.has(task.fileUrl) &&
          !this.downloadingFiles.has(task.fileUrl) &&
          !this.failedFileUrls.has(task.fileUrl),
      )
      .map(task => ({ ...task, type, sectionId }))

    if (newTasks.length === 0) {
      return
    }

    // 섹션 등록
    if (sectionId) {
      this.sections.set(sectionId, {
        total: newTasks.length,
        completed: 0,
        callback: progressCallback || null,
      })
    }

    // 호출 시점의 signal을 캡처하여 전달 (과업 전환 시 이전 signal이 abort됨)
    const signal = this.abortController?.signal
    await this.processFiles(newTasks, type, signal)
  }

  /**
   * 타일 다운로드 추가 (백그라운드)
   * @param tileFloorplans - 타일 이미지 설정 정보를 포함한 도면 배열
   * @param taskId - 과업 ID
   * @returns 다운로드 완료 Promise
   */
  async addTileDownloads(
    tileFloorplans: Array<{
      facilityName: string
      floorplan: Floorplan
    }>,
    taskId: string,
  ): Promise<void> {
    // 레벨 단위로 청킹하여 메인 스레드 블로킹 방지
    for (const { facilityName: _facilityName, floorplan } of tileFloorplans) {
      if (!floorplan.is_tiled || !floorplan.tile_config || !floorplan.tile_base_url) continue

      const { tile_base_url, tile_config } = floorplan
      const { floorplanId, createdAt, levels } = tile_config

      for (const level of levels) {
        const { cols, rows, scale } = level
        const levelTasks: FileDownloadTaskBase[] = []

        for (let col = 0; col < cols; col++) {
          for (let row = 0; row < rows; row++) {
            const fileName = `${floorplanId}_${scale * 100}_${col}_${row}_${createdAt}`
            const tileUrl = `${tile_base_url}/x${scale}/${fileName}.webp`

            if (this.downloadedFiles.has(tileUrl) || this.downloadingFiles.has(tileUrl)) {
              continue
            }

            levelTasks.push({
              taskId,
              fileUrl: tileUrl,
              name: fileName,
            })
          }
        }

        if (levelTasks.length > 0) {
          // 네이티브 직접 다운로드로 JS 메모리 점유가 없어 고정 대기 불필요 — 레벨 간 yield만 수행
          await new Promise<void>(r => setTimeout(r, 0))
          await this.addDownloadBatch({
            taskId,
            tasks: levelTasks,
            type: 'tile',
          })
        }
      }
    }
  }

  // 성공 처리 공통 헬퍼 (signal이 abort된 경우 상태 변경 스킵)
  private markCompleted(task: FileDownloadTask, signal?: AbortSignal): void {
    if (signal?.aborted) return
    this.downloadingFiles.delete(task.fileUrl)
    this.downloadedFiles.add(task.fileUrl)
    this.addToDirListing(this.extractFilePath(task.fileUrl, task.type))
    if (task.sectionId) {
      this.updateSectionProgress(task.sectionId)
    }
  }

  /**
   * 파일 다운로드 처리
   * @param signal - 호출 시점에 캡처된 AbortSignal. 과업 전환 시 이 signal이 abort됨.
   */
  private async processFiles(
    tasks: FileDownloadTask[],
    type: FileType,
    signal?: AbortSignal,
  ): Promise<void> {
    if (signal?.aborted) return
    // 타일은 최후순위: 캐시 확인(readdir)부터 층 진입 등 우선 작업 뒤로 미룬다
    if (type === 'tile') {
      await this.waitTileResume(signal)
      if (signal?.aborted) return
    }

    // 배치 레벨 네트워크 체크 (1회)
    const networkStatus = await Network.getStatus()
    if (!networkStatus.connected) {
      for (const task of tasks) {
        this.addToFailedQueue(task, signal)
      }
      return
    }

    // readdir 1회로 이미 캐시된 파일 일괄 필터링
    let tasksToDownload: FileDownloadTask[]
    if (type === 'tile') {
      tasksToDownload = await this.filterExistingTiles(tasks, signal)
    } else {
      tasksToDownload = await this.filterExistingFiles(tasks, signal)
    }
    if (tasksToDownload.length === 0) return

    const running: Promise<void>[] = []

    for (const task of tasksToDownload) {
      if (signal?.aborted) return

      this.downloadingFiles.add(task.fileUrl)

      const downloadPromise = this.downloadSingleFile(task, signal)

      const p = downloadPromise.finally(() => {
        const i = running.indexOf(p)
        if (i >= 0) running.splice(i, 1)
      })

      running.push(p)

      // 타일은 네이티브 직접 다운로드로 JS 메모리 점유가 없어 동시 수를 4까지 허용
      const concurrency = type === 'tile' ? 4 : this.BATCH_SIZE
      if (running.length >= concurrency) {
        await Promise.race(running)
      }
    }

    await Promise.allSettled(running)

    if (!signal?.aborted && this.failedFiles.length > 0) {
      this.startRetryInterval()
    }
  }

  // readdir 1회로 PHOTO_DIR 내 기존 파일을 일괄 확인하여 필터링
  private async filterExistingFiles(
    tasks: FileDownloadTask[],
    signal?: AbortSignal,
  ): Promise<FileDownloadTask[]> {
    // 기존 파일 목록 (세션 메모이즈)
    const existingPaths = await this.getDirListing(PHOTO_DIR)

    const toDownload: FileDownloadTask[] = []
    for (const task of tasks) {
      const fileName = task.fileUrl.split('/').pop() ?? ''
      if (existingPaths.has(`${PHOTO_DIR}/${fileName}`)) {
        this.markCompleted(task, signal)
      } else {
        toDownload.push(task)
      }
    }

    return toDownload
  }

  // 타일 디렉토리를 일괄 조회하여 이미 존재하는 파일을 필터링
  private async filterExistingTiles(
    tasks: FileDownloadTask[],
    signal?: AbortSignal,
  ): Promise<FileDownloadTask[]> {
    // 고유 scale 디렉토리 수집
    const scaleDirs = new Set<string>()
    for (const task of tasks) {
      const urlParts = task.fileUrl.split('/')
      const scale = urlParts[urlParts.length - 2]
      scaleDirs.add(`${TILE_DIR}/${scale}`)
    }

    // 디렉토리별 기존 파일 목록 (세션 메모이즈)
    const listings = await Promise.all(Array.from(scaleDirs).map(dir => this.getDirListing(dir)))
    const existingPaths = new Set<string>()
    for (const listing of listings) for (const path of listing) existingPaths.add(path)

    // 이미 존재하는 파일은 즉시 완료 처리, 나머지만 반환
    const toDownload: FileDownloadTask[] = []
    for (const task of tasks) {
      const filePath = this.extractFilePath(task.fileUrl, 'tile')
      if (existingPaths.has(filePath)) {
        this.markCompleted(task, signal)
      } else {
        toDownload.push(task)
      }
    }

    return toDownload
  }

  // 상위 디렉토리 보장 (Android downloadFile은 부모 디렉토리를 만들지 않으므로 선행 mkdir 필요)
  private async ensureDir(dirPath: string): Promise<void> {
    if (this.ensuredDirs.has(dirPath)) return
    try {
      await Filesystem.mkdir({
        path: dirPath,
        directory: this.STORAGE_DIRECTORY,
        recursive: true,
      })
    } catch {
      // 이미 존재하는 경우 무시
    }
    this.ensuredDirs.add(dirPath)
  }

  /**
   * 단일 파일 다운로드 (네이티브 직접 저장 — JS 측 blob/base64/브리지 전송 없음)
   * HTTP 4xx/5xx는 네이티브에서 reject되어 실패 큐로 이동한다 (Android: getInputStream throw, iOS: statusCode 검사)
   * @param signal - 캡처된 AbortSignal. abort 시 상태 변경을 스킵.
   */
  private async downloadSingleFile(task: FileDownloadTask, signal?: AbortSignal): Promise<void> {
    if (signal?.aborted) return
    // 타일은 최후순위: 층 진입 등 우선 작업 중에는 게이트에서 대기
    if (task.type === 'tile') {
      await this.waitTileResume(signal)
      if (signal?.aborted) return
    }

    const filePath = this.extractFilePath(task.fileUrl, task.type)
    const dirPath = filePath.substring(0, filePath.lastIndexOf('/'))
    // 최종 경로에 직접 스트리밍하면 강제종료 시 잘린 파일이 남아 캐시 적중으로 오인되므로
    // 임시 경로에 받은 뒤 rename(원자적)으로 최종 경로에 놓는다
    const tempPath = `${filePath}.part`

    try {
      const signedUrl = CloudFile.ResourceUrlToSignedUrl(task.fileUrl)
      await this.ensureDir(dirPath)

      await Filesystem.downloadFile({
        url: signedUrl,
        path: tempPath,
        directory: this.STORAGE_DIRECTORY,
      })
      if (signal?.aborted) {
        await Filesystem.deleteFile({ path: tempPath, directory: this.STORAGE_DIRECTORY }).catch(
          () => {},
        )
        return
      }
      await Filesystem.rename({
        from: tempPath,
        to: filePath,
        directory: this.STORAGE_DIRECTORY,
        toDirectory: this.STORAGE_DIRECTORY,
      })

      this.markCompleted(task, signal)
    } catch (error) {
      // 중단된 다운로드의 부분 파일이 이후 캐시 적중으로 오인되지 않도록 제거
      await Filesystem.deleteFile({ path: tempPath, directory: this.STORAGE_DIRECTORY }).catch(
        () => {},
      )
      // 디렉토리 문제였을 가능성에 대비해 재시도 시 mkdir을 다시 수행
      this.ensuredDirs.delete(dirPath)
      if (signal?.aborted) return
      console.warn('[FileDownloadManager] Download failed:', task.name, error)
      this.addToFailedQueue(task, signal)
    }
  }

  /**
   * storage:// URL에서 파일 경로 추출
   * - tile: TILE_DIR/{scale}/{fileName}
   * - file: PHOTO_DIR/{fileName}
   */
  private extractFilePath(storageUrl: string, type: FileType): string {
    const urlParts = storageUrl.split('/')
    const fileName = urlParts[urlParts.length - 1]

    if (type === 'tile') {
      const scale = urlParts[urlParts.length - 2] // x1, x2, x4 등
      return `${TILE_DIR}/${scale}/${fileName}`
    } else {
      return `${PHOTO_DIR}/${fileName}`
    }
  }

  // 실패 큐에 추가 (signal이 abort된 경우 스킵)
  private addToFailedQueue(task: FileDownloadTask, signal?: AbortSignal): void {
    if (signal?.aborted) return
    if (this.failedFileUrls.has(task.fileUrl)) {
      return
    }

    this.downloadingFiles.delete(task.fileUrl)
    this.failedFiles.push(task)
    this.failedFileUrls.add(task.fileUrl)
    this.startRetryInterval()
  }

  // 재시도 interval 시작
  private startRetryInterval(): void {
    if (this.retryInterval) return

    this.retryInterval = setInterval(async () => {
      if (this.failedFiles.length === 0) {
        if (this.retryInterval) {
          clearInterval(this.retryInterval)
          this.retryInterval = null
        }
        return
      }

      const networkStatus = await Network.getStatus()
      if (networkStatus.connected) {
        await this.retryFailedFiles()
      } else {
        console.info('[FileDownloadManager] Waiting for network, failed:', this.failedFiles.length)
      }
    }, this.RETRY_CHECK_INTERVAL)
  }

  // 실패한 파일 재시도
  private async retryFailedFiles(): Promise<void> {
    if (this.failedFiles.length === 0) return

    const filesToRetry = [...this.failedFiles]
    this.failedFiles = []
    this.failedFileUrls.clear()

    // 재시도 시점의 signal 캡처
    const signal = this.abortController?.signal

    const running: Promise<void>[] = []

    for (const task of filesToRetry) {
      if (signal?.aborted) return

      this.downloadingFiles.add(task.fileUrl)

      const p = this.downloadSingleFile(task, signal).finally(() => {
        const i = running.indexOf(p)
        if (i >= 0) running.splice(i, 1)
      })

      running.push(p)

      if (running.length >= this.BATCH_SIZE) {
        await Promise.race(running)
      }
    }

    await Promise.allSettled(running)
  }

  // 섹션 진행률 업데이트
  private updateSectionProgress(sectionId: string): void {
    const section = this.sections.get(sectionId)
    if (!section) return

    section.completed++

    if (section.completed > section.total) {
      section.completed = section.total
    }

    if (section.callback) {
      const percentage =
        section.completed >= section.total
          ? 100
          : Math.min(Math.floor((section.completed / section.total) * 100), 99)
      section.callback(section.completed, section.total, percentage)
    }
  }

  // 진행 중인 다운로드를 중단 (백그라운드 진입 시 메모리 확보용)
  // 다음 startNewTask 호출 시 새 AbortController가 생성되므로 재개 가능
  pauseDownloads(): void {
    this.abortController?.abort()
    this.abortController = null

    if (this.retryInterval) {
      clearInterval(this.retryInterval)
      this.retryInterval = null
    }

    this.downloadingFiles.clear()
    this.failedFiles = []
    this.failedFileUrls.clear()
    // 게이트에 대기 중인 다운로드가 영구 대기하지 않도록 해제 (abort 후 signal 체크로 종료됨)
    this.releaseTileGate()
  }

  // 초기화
  private reset(): void {
    this.abortController?.abort()
    this.abortController = null

    if (this.retryInterval) {
      clearInterval(this.retryInterval)
      this.retryInterval = null
    }

    this.downloadingFiles.clear()
    this.downloadedFiles.clear()
    this.failedFiles = []
    this.failedFileUrls.clear()
    this.sections.clear()
    this.dirListingCache.clear()
    this.releaseTileGate()
  }

  /**
   * 섹션 진행 상태 조회
   * @param sectionId - 섹션 ID
   * @returns 진행 상태 (현재 완료 수, 전체 수, 진행률)
   */
  getSectionProgress(sectionId: string): { current: number; total: number; percentage: number } {
    const section = this.sections.get(sectionId)

    if (!section) {
      return { current: 0, total: 0, percentage: 0 }
    }

    const percentage = section.total > 0 ? Math.round((section.completed / section.total) * 100) : 0

    return {
      current: section.completed,
      total: section.total,
      percentage,
    }
  }
}
